import React, { useContext, useMemo } from 'react';

import SessionChart from '@totem/components/services/remoteAccess/sessionChart/sessionChart';
import { SessionDataPoint } from '@totem/components/services/remoteAccess/sessionChart/types';
import RemoteAccessDashboardContext from '@totem/components/services/remoteAccess/v2/summary/remoteAccessDashboardContext';
import { isNull } from '@totem/utilities/common';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const getDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

const UserSessionActivity = () => {
  const { dashboardData } = useContext(RemoteAccessDashboardContext);

  const chartData = useMemo(() => {
    if (
      isNull(dashboardData) ||
      !Array.isArray(dashboardData.userSessionActivity) ||
      dashboardData.userSessionActivity.length === 0
    ) {
      return [];
    }

    const dailyStats = new Map<
      string,
      { count: number; totalDurationHours: number }
    >();
    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    dashboardData.userSessionActivity.forEach((item) => {
      const date = new Date(item.year, item.month - 1, item.day);
      const dateKey = getDateKey(date);
      const existing = dailyStats.get(dateKey) || {
        count: 0,
        totalDurationHours: 0,
      };
      const nextCount = existing.count + item.count;
      const nextTotalDurationHours =
        existing.totalDurationHours + (item.avgDuration / 60) * item.count;

      dailyStats.set(dateKey, {
        count: nextCount,
        totalDurationHours: nextTotalDurationHours,
      });

      if (minDate === null || date < minDate) {
        minDate = date;
      }

      if (maxDate === null || date > maxDate) {
        maxDate = date;
      }
    });

    if (minDate === null || maxDate === null) {
      return [];
    }

    const continuousChartData: SessionDataPoint[] = [];
    const currentDate = new Date(minDate.getTime());

    while (currentDate.getTime() <= maxDate.getTime()) {
      const date = new Date(currentDate.getTime());
      const daily = dailyStats.get(getDateKey(date));
      const count = daily?.count || 0;
      const avgDurationHours = count > 0 ? daily.totalDurationHours / count : 0;

      continuousChartData.push({
        date,
        count,
        avgDurationHours,
      });

      currentDate.setTime(currentDate.getTime() + DAY_IN_MS);
    }

    return continuousChartData;
  }, [dashboardData]);

  return <SessionChart sessionData={chartData} />;
};

export default UserSessionActivity;
